import { FunctionComponent, ReactNode, useCallback, useState } from 'react';

import '@reach/dialog/styles.css';

import {
  DialogContent,
  DialogContentProps,
  DialogOverlay,
  DialogOverlayProps,
} from '@reach/dialog';
import { m as motion } from 'framer-motion';
import { useMedia } from 'react-use';
import styled from 'styled-components';

import { CloseIcon } from 'chime-ui/components/Icons';
import { values } from 'chime-ui/system';
import media, { screenSizes } from 'chime-ui/system/media';

import { GhostButton, SecondaryButton } from '../Button';

type BottomSheetProps = {
  onShow?: () => void;
  onDismiss?: () => void;
};

type BottomSheetReturn = {
  Modal: FunctionComponent<ModalProps>;
  isShowing: boolean;
  handleShow: () => void;
  handleDismiss: () => void;
};

type ModalProps = {
  children: ReactNode;
  showX?: boolean;
  dismissText?: string;
  handleDismiss: () => void;
};

const useBottomSheet = ({
  onShow = () => undefined,
  onDismiss = () => undefined,
}: BottomSheetProps = {}): BottomSheetReturn => {
  const [isShowing, setIsShowing] = useState(false);

  const handleShow = useCallback(() => {
    setIsShowing(true);
    onShow();
  }, [onShow]);

  const handleDismiss = useCallback(() => {
    setIsShowing(false);
    onDismiss();
  }, [onDismiss]);

  function Modal({
    children,
    showX = false,
    handleDismiss,
    dismissText = 'Got it',
  }) {
    const isTablet = useMedia(`(min-width: ${screenSizes.tablet}px)`);
    const shouldShowX = isTablet || showX;

    const contentAnimationProps = isTablet
      ? {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
        }
      : {
          initial: { y: '100%' },
          animate: { y: 0 },
          exit: { y: '100%' },
        };

    const MotionDialogOverlay = motion<DialogOverlayProps>(
      StyledDialogOverlay,
      { forwardMotionProps: true }
    );

    const MotionDialogContent = motion<DialogContentProps>(
      StyledDialogContent,
      { forwardMotionProps: true }
    );

    return (
      <MotionDialogOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ type: 'easeIn', delay: values.TIMEOUT_DELAY }}
        onDismiss={handleDismiss}
      >
        <MotionDialogContent
          aria-label="Info Modal"
          transition={{ type: 'easeIn', delay: values.TIMEOUT_DELAY }}
          {...contentAnimationProps}
        >
          <Message>
            {shouldShowX && <CloseIconButton onClick={handleDismiss} />}
            {children}
          </Message>

          {!isTablet && (
            <SecondaryButton onClick={handleDismiss}>
              {dismissText}
            </SecondaryButton>
          )}
        </MotionDialogContent>
      </MotionDialogOverlay>
    );
  }

  return {
    Modal,
    isShowing,
    handleShow,
    handleDismiss,
  };
};

export default useBottomSheet;

const CloseIconButton = styled(GhostButton).attrs(() => ({
  children: <CloseIcon />,
}))`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledDialogOverlay = styled(DialogOverlay)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: hsla(0, 0%, 0%, 0.6);
  z-index: 1;
`;

const StyledDialogContent = styled(DialogContent)`
  font-family: ${props => props.theme.fonts.primaryFont};
  background: ${props => props.theme.colors.white};
  position: fixed;
  padding: 1.5rem;
  display: block;
  max-height: 80vh;
  overflow: auto;
  margin: 0;
  width: auto;

  ${media.mobileOnly`
    left: 0;
    right: 0;
    bottom: 0;
    box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem 0.5rem 0 0;
  `}

  ${media.tablet`
    top: 9.75rem;
    width: 37.5rem;
    border-radius: 0.5rem;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.25);
  `}
`;

const Message = styled.div`
  position: relative;
  margin: 0;
  span {
    white-space: pre-wrap;
  }
`;
